import React from 'react';
import { Gem, Lock, ListOrdered, Zap, Coins, Landmark, ArrowRight, UserX, UserCheck } from 'lucide-react';
import { useT } from '../../i18n';

const STEPS = [
  { icon: Gem, key: 'own' },
  { icon: Lock, key: 'stake' },
  { icon: ListOrdered, key: 'queue' },
  { icon: Zap, key: 'execute' },
  { icon: Coins, key: 'earn' },
  { icon: Landmark, key: 'govern' },
];

const PLATFORMS = ['BTC · ETH · POL · CRANNI', 'Bull vs Bear', 'BullsEye', 'Cranni Bestiarium', 'Cynths ciBOLD'];

/** One execution, drawn: the node in line gets a turn window; offline nodes are skipped, never block */
const TurnTimeline = () => {
  const t = useT();
  const slots = [
    { node: '#11', state: 'skipped', icon: UserX, tone: 'border-rose-400/40 bg-rose-400/10 text-rose-200' },
    { node: '#12', state: 'runs', icon: UserCheck, tone: 'border-emerald-400/50 bg-emerald-400/15 text-emerald-200' },
    { node: '#13', state: 'next', icon: ListOrdered, tone: 'border-white/15 bg-white/5 text-white/50' },
  ];
  return (
    <div className="min-w-0 rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-6">
      <p className="text-xs uppercase tracking-[0.3em] text-white/50">{t('flow.timelineTitle')}</p>
      <div className="mt-4 flex items-stretch gap-2 overflow-x-auto pb-1">
        <div className="flex min-w-[96px] flex-col justify-center rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-3 text-center">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-amber-200">{t('flow.due')}</p>
          <p className="mt-1 text-xs text-white/70">{t('flow.dueSub')}</p>
        </div>
        {slots.map((slot) => (
          <React.Fragment key={slot.node}>
            <ArrowRight className="h-5 w-5 shrink-0 self-center text-white/30" aria-hidden="true" />
            <div className={`flex min-w-[120px] flex-1 flex-col items-center justify-center rounded-xl border px-3 py-3 text-center ${slot.tone}`}>
              <slot.icon className="h-5 w-5" aria-hidden="true" />
              <p className="notranslate mt-1 font-mono text-sm font-bold">Node {slot.node}</p>
              <p className="text-[0.7rem]">{t(`flow.slot.${slot.state}`)}</p>
            </div>
          </React.Fragment>
        ))}
      </div>
      <p className="mt-3 text-xs text-white/55">{t('flow.timelineNote')}</p>
    </div>
  );
};

/**
 * Why NFT nodes exist and how they work, as a picture: own -> stake -> queue -> execute -> earn -> govern.
 * Shown on the NFTnodes "about" tab.
 */
const NodesFlow = () => {
  const t = useT();
  return (
    <section className="mt-10 rounded-[32px] border border-white/10 bg-gradient-to-br from-[#140f19] via-[#130f19] to-[#2d2139] p-6 text-white shadow-2xl shadow-blue-900/30 sm:p-10">
      <div className="text-center">
        <p className="text-sm uppercase tracking-[0.4em] text-amber-300">{t('flow.eyebrow')}</p>
        <h2 className="mt-3 text-3xl font-extrabold uppercase">{t('flow.title')}</h2>
        <p className="mx-auto mt-3 max-w-3xl text-white/75">{t('flow.why')}</p>
      </div>

      <ol className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {STEPS.map((step, index) => (
          <li key={step.key} className="relative rounded-2xl border border-white/10 bg-black/40 p-4">
            <div className="flex items-center gap-3">
              <span className="notranslate flex h-8 w-8 shrink-0 items-center justify-center rounded-full lb-fill-brand text-sm font-bold text-white">
                {index + 1}
              </span>
              <step.icon className="h-5 w-5 text-amber-300" aria-hidden="true" />
            </div>
            <h3 className="mt-3 font-bold">{t(`flow.step.${step.key}.title`)}</h3>
            <p className="mt-1 text-sm text-white/65">{t(`flow.step.${step.key}.text`)}</p>
            {index < STEPS.length - 1 ? (
              <ArrowRight
                className="absolute -right-3 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 rounded-full bg-[#130f19] text-amber-300 lg:block"
                aria-hidden="true"
              />
            ) : null}
          </li>
        ))}
      </ol>

      <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr,1.1fr]">
        <div className="min-w-0 rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50">{t('flow.platformsTitle')}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {PLATFORMS.map((platform) => (
              <span key={platform} className="notranslate rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-sm text-cyan-100">
                {platform}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm text-white/65">{t('flow.platformsText')}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-3">
              <p className="font-bold text-amber-200">{t('flow.golden')}</p>
              <p className="text-white/65">{t('flow.goldenText')}</p>
            </div>
            <div className="rounded-xl border border-sky-400/30 bg-sky-400/10 p-3">
              <p className="font-bold text-sky-200">{t('flow.common')}</p>
              <p className="text-white/65">{t('flow.commonText')}</p>
            </div>
          </div>
        </div>
        <TurnTimeline />
      </div>
    </section>
  );
};

export default NodesFlow;
